// Extracted from original/src/components/WalletStatementFilterModal.tsx:1-645 (the "تخصيص فلتر كشف" dialog opened by
// "كشف" on a box / vault card). Kept verbatim: backdrop + dialog frame (:316-336), gradient header with kind icon,
// badge, balance line and reset / close (:338-396), section 1 date presets + EnglishDatePicker pair (:401-470),
// section 2 operator chips (:472-524), section 3 movement-type grid (:526-571), section 4 matching preview (:573-608)
// and the footer (:612-641). Cut: internal filter state (→ controlled `values` + `onChange`), the preset → date
// arithmetic (:64-113 → `onPresetSelect`, the container computes the dates from its clock), `availableOperators`
// derived from movements/users (:115-140 → `operators` prop with optional `count`), `filteredPreview` / `previewStats`
// (:202-280 → `matchingCount`, `totalCount`, `totalInLabel`, `totalOutLabel` — formatted by the container, unit
// included), `formatNumber` of the wallet balances (:367-371 → `balanceLabel` / `balanceUsdLabel`, unit included),
// `wallet.type === 'vault'` (:312 → `kind` prop, brief rule 10), `isOpen` gating (the container mounts the modal).
import { motion } from 'motion/react';
import {
  Calendar,
  User,
  Filter,
  X,
  Check,
  RotateCcw,
  ArrowDownLeft,
  ArrowUpRight,
  ArrowLeftRight,
  Zap,
  Package,
  DollarSign,
  TrendingUp,
  ShieldCheck,
  Grip,
  Sparkles,
  Layers,
  FileText,
} from 'lucide-react';
import { useI18n } from '../i18n-context.js';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';

export type WalletStatementKind = 'vault' | 'dollar_vault' | 'currency_vault' | 'digital_box' | 'custody_iqd' | 'custody_usd';

export type WalletFilterPreset = 'all' | 'today' | 'yesterday' | 'last7' | 'thisMonth' | 'lastMonth' | 'custom';

export interface WalletFilterValues {
  readonly preset: WalletFilterPreset;
  /** YYYY-MM-DD or "". */
  readonly startDate: string;
  readonly endDate: string;
  /** Operator id, or "" = all employees. */
  readonly operator: string;
  /** "" = all types, otherwise one of WALLET_FILTER_TYPES. */
  readonly movementType: string;
}

export interface WalletFilterOperator {
  readonly id: string;
  readonly label: string;
  /** Movements by this operator in the box; the count badge renders only when > 0 (old :512). */
  readonly count?: number;
}

export interface WalletStatementFilterModalProps {
  readonly kind: WalletStatementKind;
  readonly walletName: string;
  /** Current balance with unit, formatted by the container (old `formatNumber(balance) + " د.ع"`). */
  readonly balanceLabel: string;
  /** Second balance line with unit (old `formatNumber(balance_usd) + " $"`); omitted = not rendered. */
  readonly balanceUsdLabel?: string | null;
  readonly operators: readonly WalletFilterOperator[];
  readonly values: WalletFilterValues;
  readonly onChange: (patch: Partial<WalletFilterValues>) => void;
  /** The container sets `preset` + the matching `startDate` / `endDate` (old `setPreset`, :73-113). */
  readonly onPresetSelect: (preset: Exclude<WalletFilterPreset, 'custom'>) => void;
  readonly onReset: () => void;
  /** Preview figures (old `previewStats`), computed by the container. */
  readonly matchingCount: number;
  readonly totalCount: number;
  readonly totalInLabel: string;
  readonly totalOutLabel: string;
  readonly onApply: () => void;
  /** "عرض الكشف كاملاً مباشرة": open the unfiltered statement. */
  readonly onViewAll: () => void;
  readonly onClose: () => void;
}

/** Old type ids (:143-200) with their icon and colour classes; "" stands for the old 'all'. */
export const WALLET_FILTER_TYPES = [
  { id: '', key: 'all', icon: Layers, color: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-100 dark:bg-slate-800' },
  { id: 'incoming', key: 'incoming', icon: ArrowDownLeft, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/40' },
  { id: 'outgoing', key: 'outgoing', icon: ArrowUpRight, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-950/40' },
  { id: 'transfer', key: 'transfer', icon: ArrowLeftRight, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/40' },
  { id: 'manual_work', key: 'manual_work', icon: Zap, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/40' },
  { id: 'product', key: 'product', icon: Package, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-950/40' },
  { id: 'trade', key: 'trade', icon: DollarSign, color: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-950/40' },
  { id: 'commission', key: 'commission', icon: TrendingUp, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/40' },
] as const;

const PRESETS: readonly { id: Exclude<WalletFilterPreset, 'custom'>; key: string }[] = [
  { id: 'all', key: 'wallet_filter.preset_all' },
  { id: 'today', key: 'wallet_filter.preset_today' },
  { id: 'yesterday', key: 'wallet_filter.preset_yesterday' },
  { id: 'last7', key: 'wallet_filter.preset_last7' },
  { id: 'thisMonth', key: 'wallet_filter.preset_this_month' },
  { id: 'lastMonth', key: 'wallet_filter.preset_last_month' },
];

export function WalletStatementFilterModal(props: WalletStatementFilterModalProps) {
  const { t, locale } = useI18n();
  const isAr = locale === 'ar';
  const v = props.values;
  const isVault = props.kind === 'vault' || props.kind === 'dollar_vault' || props.kind === 'currency_vault';
  const selectedOperator = props.operators.find((op) => op.id === v.operator);
  const selectedType = WALLET_FILTER_TYPES.find((x) => x.id === v.movementType);

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-4 md:p-6 select-none overflow-y-auto"
      style={{ direction: isAr ? 'rtl' : 'ltr' }}
      data-testid="wallet-filter-modal"
    >
      {/* Backdrop with subtle blur */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={props.onClose}
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-md transition-opacity"
      />

      {/* Modal Container */}
      <motion.div
        initial={{ scale: 0.94, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.94, opacity: 0, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-white dark:bg-[#0d131f] w-full max-w-2xl rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col my-auto max-h-[92vh]"
      >
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex justify-between items-center border-b border-slate-800/80 shrink-0">
          <div className="flex items-center gap-3.5">
            <div
              className={`w-11 h-11 rounded-2xl flex items-center justify-center shadow-lg ${
                isVault
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30 shadow-blue-500/10'
                  : 'bg-orange-500/20 text-orange-400 border border-orange-500/30 shadow-orange-500/10'
              }`}
            >
              {isVault ? <ShieldCheck size={24} /> : <Grip size={24} />}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-black tracking-tight">
                  {t('wallet_filter.title', { name: props.walletName })}
                </h3>
                <span
                  className={`text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider ${
                    isVault
                      ? 'bg-blue-500/20 text-blue-300 border border-blue-400/30'
                      : 'bg-orange-500/20 text-orange-300 border border-orange-400/30'
                  }`}
                >
                  {isVault ? t('wallet_filter.badge_vault') : t('wallet_filter.badge_digital')}
                </span>
              </div>
              <p className="text-[11px] text-slate-300 font-semibold mt-0.5 flex items-center gap-2">
                <span>{t('wallet_filter.current_balance')}</span>
                <span className="font-mono font-bold text-amber-300" dir="ltr">
                  {props.balanceLabel}
                </span>
                {props.balanceUsdLabel ? (
                  <span className="font-mono font-bold text-emerald-300" dir="ltr">
                    / {props.balanceUsdLabel}
                  </span>
                ) : null}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={props.onReset}
              data-testid="wallet-filter-reset"
              title={t('wallet_filter.reset_title')}
              className="px-2.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-slate-300 hover:text-white text-xs font-bold transition-all flex items-center gap-1 cursor-pointer"
            >
              <RotateCcw size={13} />
              <span className="hidden sm:inline">{t('wallet_filter.reset')}</span>
            </button>
            <button
              type="button"
              onClick={props.onClose}
              data-testid="wallet-filter-close-x"
              className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-rose-500/20 hover:text-rose-300 transition-colors cursor-pointer"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Scrollable Content Body */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-6 custom-scrollbar dark:bg-[#0d131f]">

          {/* SECTION 1: DATE FILTER */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black text-slate-700 dark:text-slate-300 flex items-center gap-2 uppercase tracking-wide">
                <Calendar size={15} className="text-indigo-600 dark:text-indigo-400" />
                <span>{t('wallet_filter.section_date')}</span>
              </label>
              {(v.startDate || v.endDate) && (
                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/50 px-2 py-0.5 rounded-md">
                  {v.startDate || '...'} ⬅️ {v.endDate || '...'}
                </span>
              )}
            </div>

            {/* Quick Date Presets */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  data-testid={`wallet-filter-preset-${preset.id}`}
                  onClick={() => props.onPresetSelect(preset.id)}
                  className={`py-2 px-1 text-center text-xs font-bold rounded-xl transition-all border cursor-pointer ${
                    v.preset === preset.id
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-600/20 font-black scale-[1.02]'
                      : 'bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 border-slate-200/80 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  {t(preset.key)}
                </button>
              ))}
            </div>

            {/* Custom Date Inputs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div className="space-y-1">
                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">
                  {t('wallet_filter.from_date')}
                </span>
                <EnglishDatePicker
                  value={v.startDate}
                  onChange={(val) => props.onChange({ startDate: val, preset: 'custom' })}
                  className="!py-2 !text-xs !bg-slate-50 dark:!bg-slate-900/80"
                />
              </div>

              <div className="space-y-1">
                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">
                  {t('wallet_filter.to_date')}
                </span>
                <EnglishDatePicker
                  value={v.endDate}
                  onChange={(val) => props.onChange({ endDate: val, preset: 'custom' })}
                  className="!py-2 !text-xs !bg-slate-50 dark:!bg-slate-900/80"
                />
              </div>
            </div>
          </div>

          {/* SECTION 2: EMPLOYEE / OPERATOR FILTER */}
          <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black text-slate-700 dark:text-slate-300 flex items-center gap-2 uppercase tracking-wide">
                <User size={15} className="text-blue-600 dark:text-blue-400" />
                <span>{t('wallet_filter.section_operator')}</span>
              </label>
              {v.operator !== '' && (
                <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 px-2 py-0.5 rounded-md">
                  {selectedOperator?.label ?? v.operator}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="wallet-filter-operator-all"
                onClick={() => props.onChange({ operator: '' })}
                className={`py-2 px-3 text-xs font-bold rounded-xl transition-all border flex items-center gap-1.5 cursor-pointer ${
                  v.operator === ''
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm shadow-blue-600/20 font-black'
                    : 'bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 border-slate-200/80 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800'
                }`}
              >
                <User size={13} />
                <span>{t('wallet_filter.all_employees')}</span>
              </button>

              {props.operators.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  data-testid={`wallet-filter-operator-${op.id}`}
                  onClick={() => props.onChange({ operator: op.id })}
                  className={`py-2 px-3 text-xs font-bold rounded-xl transition-all border flex items-center gap-1.5 cursor-pointer ${
                    v.operator === op.id
                      ? 'bg-blue-600 text-white border-blue-600 shadow-sm shadow-blue-600/20 font-black scale-[1.02]'
                      : 'bg-slate-50 dark:bg-slate-900/60 text-slate-600 dark:text-slate-400 border-slate-200/80 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  <span>{op.label}</span>
                  {(op.count ?? 0) > 0 && (
                    <span
                      className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                        v.operator === op.id ? 'bg-white/20 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {op.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* SECTION 3: MOVEMENT TYPE FILTER */}
          <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <label className="text-xs font-black text-slate-700 dark:text-slate-300 flex items-center gap-2 uppercase tracking-wide">
                <Filter size={15} className="text-emerald-600 dark:text-emerald-400" />
                <span>{t('wallet_filter.section_type')}</span>
              </label>
              {v.movementType !== '' && (
                <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/50 px-2 py-0.5 rounded-md">
                  {selectedType ? t(`wallet_filter.type_${selectedType.key}`) : undefined}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {WALLET_FILTER_TYPES.map((x) => {
                const IconComponent = x.icon;
                const isSelected = v.movementType === x.id;
                return (
                  <button
                    key={x.key}
                    type="button"
                    data-testid={`wallet-filter-type-${x.key}`}
                    onClick={() => props.onChange({ movementType: x.id })}
                    className={`p-2.5 rounded-xl border text-right flex items-center justify-between gap-2 transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-md shadow-emerald-600/20 font-black'
                        : 'bg-slate-50 dark:bg-slate-900/60 text-slate-700 dark:text-slate-300 border-slate-200/70 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                          isSelected ? 'bg-white/20 text-white' : `${x.bg} ${x.color}`
                        }`}
                      >
                        <IconComponent size={15} />
                      </div>
                      <span className="text-xs font-bold truncate">{t(`wallet_filter.type_${x.key}`)}</span>
                    </div>

                    {isSelected && <Check size={16} className="shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* SECTION 4: REAL-TIME MATCHING PREVIEW */}
          <div className="p-4 rounded-2xl bg-gradient-to-r from-slate-50 via-indigo-50/40 to-slate-50 dark:from-slate-900/80 dark:via-indigo-950/30 dark:to-slate-900/80 border border-indigo-100/80 dark:border-indigo-900/40 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                <Sparkles size={16} />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
                  {t('wallet_filter.matching_title')}
                </p>
                <p className="text-sm font-black text-slate-900 dark:text-white font-mono" data-testid="wallet-filter-matching">
                  {t('wallet_filter.matching_count', { count: props.matchingCount, total: props.totalCount })}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4 text-xs font-mono">
              <div>
                <span className="text-[10px] block text-emerald-600 dark:text-emerald-400 font-bold">
                  {t('wallet_filter.total_in')}
                </span>
                <span className="font-black text-slate-800 dark:text-slate-200">
                  {props.totalInLabel}
                </span>
              </div>
              <div className="h-7 w-px bg-slate-200 dark:bg-slate-700" />
              <div>
                <span className="text-[10px] block text-rose-600 dark:text-rose-400 font-bold">
                  {t('wallet_filter.total_out')}
                </span>
                <span className="font-black text-slate-800 dark:text-slate-200">
                  {props.totalOutLabel}
                </span>
              </div>
            </div>
          </div>

        </div>

        {/* Footer Actions */}
        <div className="p-4 sm:p-5 bg-slate-50 dark:bg-[#070a12] border-t border-slate-200 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <button
            type="button"
            onClick={props.onViewAll}
            data-testid="wallet-filter-view-all"
            className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200/70 dark:hover:bg-slate-800 text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
          >
            <FileText size={15} />
            <span>{t('wallet_filter.view_all')}</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={props.onClose}
              data-testid="wallet-filter-cancel"
              className="px-5 py-2.5 rounded-xl text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
            >
              {t('cancel')}
            </button>

            <button
              type="button"
              onClick={props.onApply}
              data-testid="wallet-filter-apply"
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-500 hover:to-indigo-500 text-white font-black text-xs uppercase tracking-wider transition-all shadow-lg shadow-primary-600/25 active:scale-95 flex items-center gap-2 cursor-pointer"
            >
              <Check size={16} />
              <span>{t('wallet_filter.apply', { count: props.matchingCount })}</span>
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
